import React, { FC, useEffect, useState } from "react";
import stl from "./index.module.scss";
import { isPresent } from "../../utils";
import { useStore } from "effector-react";
import { $currentLocale } from "../../store/shared/locales";
import cn from "classnames";
import { humanizeMinutes } from "../../utils";

type Value = number;
interface Props {
  value?: number;
  onChange?: (v: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  maxDisabled?: boolean;
  minDisabled?: boolean;
  duration?: number;
}

const minMax = (v: number, min?: number, max?: number): number => {
  if (isPresent(min) && v <= min) return min;
  if (isPresent(max) && v >= max) return max;
  return v;
};

const Quantity: FC<Props> = ({
  min,
  max,
  value,
  onChange,
  disabled,
  maxDisabled,
  minDisabled,
  duration = 60,
}) => {
  const { dateFnsLocale: locale } = useStore($currentLocale);

  const [localValue, setLocalValue] = useState<Value>(0);

  const displayValue = value ?? localValue;

  const onChanges = (v: Value) => {
    if (maxDisabled && v > displayValue) return;
    if (minDisabled && v < displayValue) return;
    onChange?.(v);
    setLocalValue(v);
  };

  useEffect(() => {
    const adjustedVal = minMax(displayValue, min, max);
    if (adjustedVal !== displayValue) {
      onChanges(minMax(displayValue, min, max));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayValue, min, max]);

  const increase = () => {
    onChanges(displayValue + duration);
  };

  const decrease = () => {
    onChanges(displayValue - duration);
  };

  return (
    <div className={cn(stl.block, stl.time)}>
      <button
        className={stl.decrease}
        onClick={decrease}
        disabled={
          disabled || minDisabled || (isPresent(min) && displayValue <= min)
        }
      />
      <input
        readOnly
        disabled={disabled}
        className={stl.input}
        value={humanizeMinutes(displayValue, locale)}
        type="text"
      />
      <button
        className={stl.increase}
        onClick={increase}
        disabled={
          disabled || maxDisabled || (isPresent(max) && displayValue >= max)
        }
      />
    </div>
  );
};

export default Quantity;
